import type { Writable } from "svelte/store";
import type { HttpConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// A web server that shows directories as pages of links. The pages cannot be
// read from another origin, so /api/http fetches and parses them. Read only.

const readOnlyMsg = "HTTP sites are read only";

class FileSystemHttp extends FS {
  constructor(config: HttpConfig) {
    super(config);
  }

  get httpConfig(): HttpConfig {
    return this.config as HttpConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await httpSiteCall(mkURL("/api/http/list", "url", this.httpConfig.url, "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("http list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // most sites do not answer other origins
    return mkURL("https://workers.filerion.com/api/fetch", "url", httpFileURL(this.httpConfig.url, path));
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    throw new Error(readOnlyMsg);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throw new Error(readOnlyMsg);
  }

  async createDirectory(path: string): Promise<void> {
    throw new Error(readOnlyMsg);
  }

  async deleteFile(path: string): Promise<void> {
    throw new Error(readOnlyMsg);
  }
}

// The address of the file at path under the site's base url.
export function httpFileURL(base: string, path: string): string {
  if (!base.endsWith("/")) {
    base += "/";
  }
  let parts = path.split("/").filter((s) => s != "");
  return base + parts.map(encodeURIComponent).join("/");
}

export async function testHttpConnection(config: HttpConfig): Promise<void> {
  await httpSiteCall(mkURL("/api/http/test", "url", config.url));
}

async function httpSiteCall(url: string): Promise<any> {
  let rsp = await fetch(url);
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `http ${rsp.status}`);
  }
  return js;
}

export function makeFileSystemHttp(config: HttpConfig): FileSystemHttp {
  return new FileSystemHttp(config);
}
